import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from "./Navbar";
import Footer from "./Footer.jsx";
import "./UpcomingEvents.css";

const UpcomingEvents = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [categories, setCategories] = useState([]);

  // Dynamic image mapping based on event type
  const getEventImage = (eventType, eventName) => {
    const eventTypeLower = eventType?.toLowerCase() || '';
    
    const imageMap = {
      'conference': 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'summit': 'https://images.unsplash.com/photo-1505373877841-8d25f95d7e23?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'launch': 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'retreat': 'https://images.unsplash.com/photo-1551818255-e6e10975bc17?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'gala': 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'workshop': 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'team building': 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'virtual': 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'tech': 'https://images.unsplash.com/photo-1505373877841-8d25f95d7e23?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'product': 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'charity': 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    };
    
    for (const [key, value] of Object.entries(imageMap)) {
      if (eventTypeLower.includes(key)) {
        return value;
      }
    }
    
    const fallbackImages = [
      'https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1515187029135-18ee286d815b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1551818255-e6e10975bc17?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    ];
    
    const hash = eventName?.length || 0;
    return fallbackImages[hash % fallbackImages.length];
  };

  // Categories for filtering
  const categoryList = [
    { id: 'all', name: 'All Events', icon: 'bi-grid-3x3' },
    { id: 'conference', name: 'Conferences', icon: 'bi-megaphone' },
    { id: 'summit', name: 'Summits', icon: 'bi-graph-up' },
    { id: 'launch', name: 'Product Launches', icon: 'bi-rocket' },
    { id: 'retreat', name: 'Retreats', icon: 'bi-tree' },
    { id: 'gala', name: 'Galas', icon: 'bi-trophy' },
    { id: 'workshop', name: 'Workshops', icon: 'bi-pencil' },
    { id: 'virtual', name: 'Virtual Events', icon: 'bi-camera-reels' }
  ];

  // Fetch events from backend
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setApiError(null);
        
        console.log("Fetching upcoming events from backend...");
        
        const response = await axios.get("https://royaleheritagefiesta.com:8007/api/events/all");
        
        if (response.data && Array.isArray(response.data)) {
          const today = new Date();
          const upcoming = response.data
            .filter(event => new Date(event.eventDate) > today)
            .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));
          
          // Add dynamic images to each event
          const upcomingWithImages = upcoming.map(event => ({
            ...event,
            img: event.img || getEventImage(event.eventType, event.eventName)
          }));
          
          setEvents(upcomingWithImages);
          setFilteredEvents(upcomingWithImages);
          
          // Extract unique categories for filtering
          const uniqueCategories = [...new Set(upcoming.map(event => event.eventType?.toLowerCase()))];
          setCategories(uniqueCategories);
        } else {
          setEvents([]);
          setFilteredEvents([]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching events:", error);
        setApiError("Could not load events from server. Please try again later.");
        setLoading(false);
        
        // Fallback sample data
        const fallbackEvents = [
          { 
            eventId: 1, 
            eventName: 'Global Tech Summit 2025', 
            eventDate: '2025-06-15', 
            place: 'Singapore', 
            eventType: 'SUMMIT',
            description: 'Join 2,500+ industry leaders for the most anticipated tech conference of the year.',
            highlights: 'Keynotes from global tech leaders, networking sessions, innovation showcase',
            img: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            totalStalls: 50,
            availableStalls: 15,
            totalSeats: 2500,
            availableSeats: 1200
          },
          { 
            eventId: 2, 
            eventName: 'Flagship Product Reveal', 
            eventDate: '2025-07-20', 
            place: 'Mumbai', 
            eventType: 'LAUNCH',
            description: 'Be the first to witness groundbreaking innovations that will shape the future.',
            highlights: 'Exclusive preview, celebrity appearances, media coverage',
            img: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            totalStalls: 30,
            availableStalls: 8,
            totalSeats: 1000,
            availableSeats: 450
          },
          { 
            eventId: 3, 
            eventName: 'Executive Board Retreat', 
            eventDate: '2025-08-10', 
            place: 'Swiss Alps', 
            eventType: 'RETREAT',
            description: 'Strategic planning and team building in the heart of the Swiss Alps.',
            highlights: 'Luxury accommodation, strategic workshops, networking dinners',
            img: 'https://images.unsplash.com/photo-1551818255-e6e10975bc17?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            totalStalls: 20,
            availableStalls: 5,
            totalSeats: 200,
            availableSeats: 75
          },
          { 
            eventId: 4, 
            eventName: 'Charity Fundraising Gala', 
            eventDate: '2025-09-05', 
            place: 'London', 
            eventType: 'GALA',
            description: 'An elegant evening of philanthropy and celebration.',
            highlights: 'Celebrity hosts, live auctions, fine dining experience',
            img: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            totalStalls: 40,
            availableStalls: 12,
            totalSeats: 500,
            availableSeats: 180
          },
          { 
            eventId: 5, 
            eventName: 'Innovation Workshop Series', 
            eventDate: '2025-10-15', 
            place: 'Bengaluru', 
            eventType: 'WORKSHOP',
            description: 'Hands-on workshops for creative problem-solving and innovation.',
            highlights: 'Expert facilitators, interactive sessions, networking opportunities',
            img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            totalSeats: 150,
            availableSeats: 85
          },
          { 
            eventId: 6, 
            eventName: 'Virtual Leadership Summit', 
            eventDate: '2025-11-20', 
            place: 'Virtual', 
            eventType: 'VIRTUAL',
            description: 'Global leadership insights from the comfort of your office.',
            highlights: 'Interactive sessions, virtual networking, downloadable resources',
            img: 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            totalSeats: 5000,
            availableSeats: 3200
          }
        ];
        
        setEvents(fallbackEvents);
        setFilteredEvents(fallbackEvents);
      }
    };

    fetchEvents();
  }, []);

  // Filter events based on category and search query
  useEffect(() => {
    let filtered = [...events];
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(event => 
        event.eventType?.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    
    // Filter by search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event => 
        event.eventName?.toLowerCase().includes(query) ||
        event.place?.toLowerCase().includes(query) ||
        event.eventType?.toLowerCase().includes(query) ||
        event.description?.toLowerCase().includes(query)
      );
    }
    
    setFilteredEvents(filtered);
  }, [selectedCategory, searchQuery, events]);

  const formatDate = (dateString) => {
    if (!dateString) return "TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return "TBA";
    }
  };

  const getDaysRemaining = (eventDate) => {
    const today = new Date();
    const event = new Date(eventDate);
    const diffTime = event - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const navigateToEventDetails = (event) => {
    navigate('/event-card', { 
      state: { 
        eventId: event.eventId,
        eventData: event 
      } 
    });
  };

  const navigateToStallBooking = (event, e) => {
    e.stopPropagation();
    navigate('/Booking_event', { 
      state: { 
        eventId: event.eventId,
        eventData: event 
      } 
    });
  };

  const navigateToTicketBooking = (event, e) => {
    e.stopPropagation();
    navigate('/BookingTicket', { 
      state: { 
        eventId: event.eventId,
        eventData: event 
      } 
    });
  };

  return (
    <>
      <Navbar />
      
      {/* Page Header */}
      <section className="upcoming-header">
        <div className="container">
          <div className="header-content">
            <h1 className="camel-heading">Upcoming Events</h1>
            <p>Discover and book the most exciting corporate events happening around the world</p>
            <div className="event-stats">
              <div className="stat-card">
                <span className="stat-number">{events.length}</span>
                <span className="stat-label">Upcoming Events</span>
              </div>
             
              <div className="stat-card">
                <span className="stat-number"  >50k+</span>
                <span className="stat-label" >Attendees</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Display Section */}
      <section className="events-display-section">
        <div className="container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading amazing events...</p>
            </div>
          ) : filteredEvents.length > 0 ? (
            <>
              <div className="events-count">
                <p>Found <strong>{filteredEvents.length}</strong> events</p>
              </div>
              
              {viewMode === 'grid' ? (
                <div className="events-grid">
                  {filteredEvents.map((event, idx) => {
                    const daysRemaining = getDaysRemaining(event.eventDate);
                    const eventImage = event.img || getEventImage(event.eventType, event.eventName);
                    
                    return (
                      <div 
                        className="event-card" 
                        key={event.eventId || idx}
                        onClick={() => navigateToEventDetails(event)}
                      >
                        <div className="event-card-image">
                          <img src={eventImage} alt={event.eventName} />
                          <div className="event-badge">
                            {daysRemaining <= 7 ? (
                              <span className="badge-soon">Soon</span>
                            ) : (
                              <span className="badge-upcoming">Upcoming</span>
                            )}
                          </div>
                        </div>
                        <div className="event-card-content">
                          <h3>{event.eventName}</h3>
                          <div className="event-meta">
                            <span><i className="bi bi-calendar3"></i> {formatDate(event.eventDate)}</span>
                            <span><i className="bi bi-clock"></i> {formatTime(event.eventDate)}</span>
                            <span><i className="bi bi-geo-alt"></i> {event.place || "TBA"}</span>
                          </div>
                          <div className="event-type">
                            <i className="bi bi-tag"></i>
                            <span>{event.eventType || "Corporate Event"}</span>
                          </div>
                          <p className="event-description">
                            {event.description || event.highlights || "Join us for this exciting corporate event filled with networking opportunities and industry insights."}
                          </p>
                          <div className="event-stats-info">
                            {event.totalSeats && (
                              <div className="stat">
                                <i className="bi bi-people"></i>
                                <span>{event.availableSeats || event.totalSeats} seats left</span>
                              </div>
                            )}
                            {event.totalStalls && (
                              <div className="stat">
                                <i className="bi bi-shop"></i>
                                <span>{event.availableStalls || event.totalStalls} stalls available</span>
                              </div>
                            )}
                            <div className="stat">
                              <i className="bi bi-hourglass-split"></i>
                              <span>{daysRemaining} days left</span>
                            </div>
                          </div>
                          <div className="event-actions" onClick={(e) => e.stopPropagation()}>
                            <button 
                              onClick={(e) => navigateToStallBooking(event, e)} 
                              className="btn-stall"
                            >
                              <i className="bi bi-shop"></i> Book Stall
                            </button>
                            <button 
                              onClick={(e) => navigateToTicketBooking(event, e)} 
                              className="btn-ticket"
                              
                            >
                              <i className="bi bi-ticket-perforated"></i> Get Tickets
                            </button>
                           
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="events-list">
                  {filteredEvents.map((event, idx) => {
                    const daysRemaining = getDaysRemaining(event.eventDate);
                    const eventImage = event.img || getEventImage(event.eventType, event.eventName);
                    
                    return (
                      <div 
                        className="event-list-item" 
                        key={event.eventId || idx}
                        onClick={() => navigateToEventDetails(event)}
                      >
                        <div className="list-item-image">
                          <img src={eventImage} alt={event.eventName} />
                        </div>
                        <div className="list-item-content">
                          <div className="list-item-header">
                            <h3>{event.eventName}</h3>
                            <div className="event-badge-list">
                              {daysRemaining <= 7 ? (
                                <span className="badge-soon">Starting Soon</span>
                              ) : (
                                <span className="badge-upcoming">In {daysRemaining} days</span>
                              )}
                            </div>
                          </div>
                          <div className="event-meta-list">
                            <span><i className="bi bi-calendar3"></i> {formatDate(event.eventDate)}</span>
                            <span><i className="bi bi-clock"></i> {formatTime(event.eventDate)}</span>
                            <span><i className="bi bi-geo-alt"></i> {event.place || "TBA"}</span>
                            <span><i className="bi bi-tag"></i> {event.eventType || "Corporate Event"}</span>
                          </div>
                          <p className="event-description-list">
                            {event.description || event.highlights || "Join us for this exciting corporate event filled with networking opportunities and industry insights."}
                          </p>
                          <div className="event-stats-list">
                            {event.totalSeats && (
                              <span><i className="bi bi-people"></i> {event.availableSeats || event.totalSeats} seats left</span>
                            )}
                            {event.totalStalls && (
                              <span><i className="bi bi-shop"></i> {event.availableStalls || event.totalStalls} stalls available</span>
                            )}
                          </div>
                          <div className="list-item-actions" onClick={(e) => e.stopPropagation()}>
                            <button 
                              onClick={(e) => navigateToStallBooking(event, e)} 
                              className="btn-stall-sm"
                              disabled={!event.totalStalls || event.availableStalls === 0}
                            >
                              <i className="bi bi-shop"></i> Book Stall
                            </button>
                            <button 
                              onClick={(e) => navigateToTicketBooking(event, e)} 
                              className="btn-ticket-sm"
                              disabled={!event.totalSeats || event.availableSeats === 0}
                            >
                              <i className="bi bi-ticket-perforated"></i> Get Tickets
                            </button>
                            <button className="btn-details-sm" onClick={() => navigateToEventDetails(event)}>
                              View Details <i className="bi bi-arrow-right"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="no-events">
              <i className="bi bi-calendar-x"></i>
              <h3>No Events Found</h3>
              <p>We couldn't find any events matching your criteria. Try adjusting your filters or check back later for new events.</p>
              <button className="btn-clear-filters" onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
              }}>
                Clear Filters
              </button>
            </div>
          )}
          
          {apiError && (
            <div className="api-error">
              <i className="bi bi-exclamation-triangle"></i>
              <p>{apiError}</p>
            </div>
          )}
        </div>
      </section>


      <Footer />
    </>
  );
};

export default UpcomingEvents;